import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type TopicRecord } from '../lib/api';

type Length = '6' | '8' | '10' | '12' | '15';

export function TopicIntakePage() {
  const queryClient = useQueryClient();
  const [topic, setTopic] = useState('');
  const [lengthMin, setLengthMin] = useState<Length>('10');
  const [tone, setTone] = useState(
    'Authoritative explainer, slightly contrarian, no jargon without explanation',
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const topicsQuery = useQuery({
    queryKey: ['topics'],
    queryFn: api.listTopics,
    refetchInterval: 5000,
  });

  const submitTopic = useMutation({
    mutationFn: api.createTopic,
    onSuccess: () => {
      setSuccessMsg('Topic submitted. Pipeline started — safety check first.');
      setErrorMsg(null);
      setTopic('');
      queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
    onError: (err: Error) => {
      setErrorMsg(err.message || 'Submission failed');
      setSuccessMsg(null);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (topic.trim().length < 5) {
      setErrorMsg('Topic must be at least 5 characters');
      return;
    }
    submitTopic.mutate({ topic: topic.trim(), length_min: lengthMin, tone });
  };

  return (
    <div className="max-w-5xl">
      <h2 className="text-3xl font-bold mb-2">Topic Intake</h2>
      <p className="text-parchment-700 mb-8">
        Submit a new topic. Pipeline runs end-to-end: safety check → research → script
        → narration → b-roll → assembly → upload.
      </p>

      <form onSubmit={onSubmit} className="card-neumorphic space-y-6">
        <div>
          <label className="block text-sm font-medium text-parchment-800 mb-2">
            Topic
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. How does the Fed actually set interest rates?"
            className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body focus:outline-none focus:ring-2 focus:ring-forest-400"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-parchment-800 mb-2">
              Target Length
            </label>
            <select
              value={lengthMin}
              onChange={(e) => setLengthMin(e.target.value as Length)}
              className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body focus:outline-none focus:ring-2 focus:ring-forest-400"
            >
              <option value="6">6 minutes</option>
              <option value="8">8 minutes</option>
              <option value="10">10 minutes</option>
              <option value="12">12 minutes</option>
              <option value="15">15 minutes</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-parchment-800 mb-2">
              Voice
            </label>
            <select
              disabled
              className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body disabled:opacity-60"
            >
              <option>Default — Channel 1</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-parchment-800 mb-2">
            Tone
          </label>
          <textarea
            rows={3}
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body focus:outline-none focus:ring-2 focus:ring-forest-400"
          />
        </div>
        {errorMsg && (
          <div className="rounded-button bg-red-100 text-red-900 px-4 py-2 text-sm">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="rounded-button bg-forest-100 text-forest-800 px-4 py-2 text-sm">
            {successMsg}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitTopic.isPending}
            className="btn-primary disabled:opacity-50"
          >
            {submitTopic.isPending ? 'Submitting…' : 'Submit Topic'}
          </button>
        </div>
      </form>
      <p className="text-xs font-mono text-parchment-700 mt-4 mb-8">
        Disney safety check runs first. Blacklisted topics blocked before any tokens
        spent.
      </p>

      <section>
        <h3 className="text-xl font-heading font-semibold mb-4">Recent Topics</h3>
        {topicsQuery.isLoading && (
          <p className="text-sm font-mono text-parchment-700">loading…</p>
        )}
        {topicsQuery.isError && (
          <p className="text-sm font-mono text-red-700">
            (api unreachable — start backend or deploy Phase 9)
          </p>
        )}
        {topicsQuery.data && topicsQuery.data.items.length === 0 && (
          <p className="text-sm font-mono text-parchment-700">no topics yet</p>
        )}
        <ul className="space-y-2">
          {topicsQuery.data?.items.map((t: TopicRecord) => (
            <li key={t.topic_id} className="card-neumorphic !p-4">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="font-medium">{t.topic}</p>
                  <p className="text-xs font-mono text-parchment-700 mt-1">
                    {t.topic_id} · {t.length_min}min · {t.status}
                  </p>
                </div>
                <span
                  className={`text-xs font-mono rounded-button px-2 py-1 ${
                    t.status === 'blocked_disney'
                      ? 'bg-red-100 text-red-900'
                      : t.status === 'complete'
                        ? 'bg-forest-100 text-forest-800'
                        : 'bg-parchment-200 text-parchment-800'
                  }`}
                >
                  {t.status}
                </span>
              </div>
              {t.safety_check_result && !t.safety_check_result.safe && (
                <p className="text-xs text-red-800 mt-2">
                  Blocked: {t.safety_check_result.reasoning}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
